import { Typography, useToast } from "@codat/orchard-ui";
import { useInviteCompanyLinkQuery } from "api/portal";
import React, { useEffect } from "react";

import ClipboardInput from "components/ClipboardInput";
import ModalBody from "components/ModalBody";

import styles from "./InviteCompanyModal.module.css";

interface InviteCompanyModalProps {
  linkUrl: string;
  onRetrieveLinkSuccess: (linkUrl: string) => void;
  onRetrieveLinkError: () => void;
  onClose: () => void;
}

const InviteCompanyModal = ({
  linkUrl,
  onRetrieveLinkSuccess,
  onRetrieveLinkError,
  onClose,
}: InviteCompanyModalProps) => {
  const { addToast } = useToast();

  const [{ loading }, getInviteCompanyLink] = useInviteCompanyLinkQuery();

  const onCopy = () => {
    addToast({
      message: "Copied invite link URL to clipboard",
      variant: "success",
    });
  };

  useEffect(() => {
    if (!linkUrl) {
      getInviteCompanyLink()
        .then((response) => {
          onRetrieveLinkSuccess(response.data);
        })
        .catch(() => {
          addToast({
            message: `Failed to load invite link url`,
            variant: "error",
          });
          onRetrieveLinkError();
        });
    }
  }, [
    linkUrl,
    getInviteCompanyLink,
    addToast,
    onRetrieveLinkSuccess,
    onRetrieveLinkError,
  ]);

  return (
    <ModalBody
      size="medium"
      title="Invite company"
      className={styles.card}
      onClose={onClose}
    >
      <Typography variant="body_md" as="p" id="company-link-url-label-text">
        This URL allows users to enter their company name and link their
        accounts, a new Codat company is generated automatically.
      </Typography>
      <Typography variant="body_md" as="p">
        This removes the need to create a company manually using the Codat
        portal or Codat API.
      </Typography>
      <ClipboardInput
        label="Company link URL"
        aria-describedby="company-link-url-label-text"
        text={linkUrl}
        className={styles.inviteLinkInput}
        onCopy={onCopy}
        isSkeleton={loading}
        id="invite-company-modal-link"
        data-testid="invite-company-modal-link"
        inputName="URL"
      />
    </ModalBody>
  );
};

export default InviteCompanyModal;
